import { loadFont } from '../dom.js';
import { ST } from '../state.js';
import { buildFactBlock } from './existing.js';

// Mixed: tokensToMap, buildTokenContext, buildMarketingBriefContext, buildImageContext are pure logic.
// syncFlatTokens, buildBusinessContext, resolveLogoSrc depend on global ST — refactor to accept ST as param during Vue migration.

/**
 * Convert keyed token array to map: [{key, value, ...}] -> { key: {value, ...} }
 * Safe for empty/undefined input; returns input as-is if already a map (not an array).
 * @param {Array<{key: string, value: string}> | Record<string, any> | undefined} arr
 * @returns {Record<string, any>}
 */
const tokensToMap = arr => {
  if (!arr) {
    return {};
  }
  if (!Array.isArray(arr)) {
    return arr;
  } // already a map (post-conversion)
  const map = {};
  arr.forEach(item => {
    map[item.key] = item;
  });
  return map;
};

/**
 * Sync flat colors/fonts from designTokens -- single source of truth for this pattern.
 * Called after concept generation, token refinement, and vision refinement.
 * @param {import('../types.ts').Concept} c
 * @returns {void}
 */
const syncFlatTokens = c => {
  const dt = c.designTokens;
  if (!dt) {
    return;
  }
  if (Array.isArray(dt.colors)) {
    dt.colors = tokensToMap(dt.colors);
  }
  c.colors = {};
  for (const k in dt.colors) {
    if (Object.hasOwn(dt.colors, k)) {
      c.colors[k] = dt.colors[k].value;
    }
  }
  c.fonts = {
    heading: dt.typography?.heading?.family || 'DM Sans',
    body: dt.typography?.body?.family || 'DM Sans',
  };
  loadFont(c.fonts.heading, c.fonts.body);
};

/**
 * Format design tokens as XML-tagged context for AI prompts.
 * @param {import('../types.ts').Concept} c
 * @returns {string}
 */
function buildTokenContext(c) {
  const tokens = c.designTokens;
  const lines = ['<design_tokens>'];

  if (tokens.colors) {
    lines.push('<colors>');
    // After concept.js processing, colors is a keyed map {primary: {key, value, name, role}}
    for (const key in tokens.colors) {
      if (Object.hasOwn(tokens.colors, key)) {
        const tok = tokens.colors[key];
        lines.push(`  --${key}: ${tok.value} ("${tok.name}") — ${tok.role}`);
      }
    }
    lines.push('</colors>');
  }

  if (tokens.typography) {
    lines.push('<typography>');
    const h = tokens.typography.heading;
    lines.push(`  Headings: ${h.family}, weights: ${h.weights} — ${h.style}`);
    const b = tokens.typography.body;
    lines.push(`  Body: ${b.family}, weights: ${b.weights} — ${b.style}`);
    const sc = tokens.typography.scale;
    lines.push(
      `  Scale: display=${sc.display}, h1=${sc.h1}, h2=${sc.h2}, h3=${sc.h3}, body=${sc.body}, small=${sc.small}`,
    );
    lines.push('</typography>');
  }

  if (tokens.spacing) {
    lines.push('<spacing>');
    lines.push(...tokens.spacing.map(s => `  ${s.key}: ${s.value}`));
    lines.push('</spacing>');
  }

  if (tokens.effects) {
    lines.push('<effects>');
    lines.push(...tokens.effects.map(e => `  --${e.key}: ${e.value} — ${e.role}`));
    lines.push('</effects>');
  }

  if (tokens.animation) {
    const dur = tokens.animation.duration || {};
    lines.push('<animation>');
    lines.push(
      `  Duration: fast=${dur.fast || '0.15s'}, medium=${dur.medium || '0.3s'}, slow=${dur.slow || '0.5s'}`,
    );
    lines.push(`  Easing: ${tokens.animation.easing}`);
    lines.push(`  Stagger: ${tokens.animation.stagger}`);
    lines.push(`  Style: ${tokens.animation.style}`);
    lines.push('</animation>');
  }

  lines.push('</design_tokens>');

  return lines.join('\n');
}

/**
 * Build XML-tagged marketing brief context for AI prompts.
 * @param {import('../types.ts').MarketingBrief} mb
 * @returns {string}
 */
const buildMarketingBriefContext = mb => {
  if (!mb) {
    return '';
  }
  const lines = [];
  lines.push('<marketing_brief>');

  if (mb.companyName) {
    lines.push(`<company>${mb.companyName}</company>`);
  }
  if (mb.sourceUrl) {
    lines.push(`<url>${mb.sourceUrl}</url>`);
  }
  if (mb.tagline) {
    lines.push(`<tagline>${mb.tagline}</tagline>`);
  }
  if (mb.industry) {
    lines.push(`<industry>${mb.industry}</industry>`);
  }
  if (mb.businessOverview) {
    lines.push(`<overview>${mb.businessOverview}</overview>`);
  }

  // Brand identity
  if (mb.brandValues?.length || mb.toneOfVoice?.length || mb.visualAesthetic?.length) {
    lines.push('<brand_identity>');
    if (mb.brandValues?.length) {
      lines.push(`  <values>${mb.brandValues.join(', ')}</values>`);
    }
    if (mb.toneOfVoice?.length) {
      lines.push(`  <tone>${mb.toneOfVoice.join(', ')}</tone>`);
    }
    if (mb.visualAesthetic?.length) {
      lines.push(`  <aesthetic>${mb.visualAesthetic.join(', ')}</aesthetic>`);
    }
    lines.push('</brand_identity>');
  }

  // Colors
  if (mb.colors?.length) {
    lines.push('<detected_colors>');
    mb.colors.forEach(c => {
      lines.push(`  ${c.hex} — "${c.name}" (${c.role})`);
    });
    lines.push('</detected_colors>');
  }

  // Fonts
  if (mb.fonts) {
    lines.push('<detected_fonts>');
    if (mb.fonts.heading) {
      lines.push(`  Heading: ${mb.fonts.heading.family} — ${mb.fonts.heading.style}`);
    }
    if (mb.fonts.body) {
      lines.push(`  Body: ${mb.fonts.body.family} — ${mb.fonts.body.style}`);
    }
    lines.push('</detected_fonts>');
  }

  // Vibes (from Q&A path)
  if (mb.vibes?.length) {
    lines.push(`<vibes>${mb.vibes.join(', ')}</vibes>`);
  }

  // Audience & positioning
  if (mb.targetAudience) {
    lines.push(`<target_audience>${mb.targetAudience}</target_audience>`);
  }
  if (mb.customerProblem) {
    lines.push(`<customer_problem>${mb.customerProblem}</customer_problem>`);
  }
  if (mb.desiredAction) {
    lines.push(
      `<desired_action>${mb.desiredAction}${mb.actionDetail ? ` — ${mb.actionDetail}` : ''}</desired_action>`,
    );
  }
  if (mb.differentiator) {
    lines.push(`<differentiator>${mb.differentiator}</differentiator>`);
  }
  if (mb.leadMagnet) {
    lines.push(`<lead_magnet>${mb.leadMagnet}</lead_magnet>`);
  }

  // Fact inventory — reuse buildFactBlock() from prompts/existing.js
  const factBlock = buildFactBlock(mb.facts);
  if (factBlock) {
    lines.push(factBlock.trim());
  }

  // Conversion signals
  if (mb.proofPoints?.length) {
    lines.push(`<proof_points>${mb.proofPoints.join(', ')}</proof_points>`);
  }
  if (mb.objections?.length) {
    lines.push(`<objections>${mb.objections.join(', ')}</objections>`);
  }
  if (mb.ctaTexts?.length) {
    lines.push(`<existing_ctas>${mb.ctaTexts.join(', ')}</existing_ctas>`);
  }
  if (mb.navLinks?.length) {
    lines.push(`<nav_links>${mb.navLinks.join(', ')}</nav_links>`);
  }
  // Social & copyright
  if (mb.socialLinks) {
    const links = Object.entries(mb.socialLinks)
      .filter(([, v]) => v)
      .map(([k, v]) => `${k}: ${v}`);
    if (links.length) {
      lines.push(`<social_links>${links.join(', ')}</social_links>`);
    }
  }
  if (mb.copyrightText) {
    lines.push(`<copyright>${mb.copyrightText}</copyright>`);
  }

  lines.push('</marketing_brief>');
  return lines.join('\n');
};

/**
 * Format business contact info as XML context for section prompts.
 * @returns {string}
 */
function buildBusinessContext() {
  const mb = ST.marketingBrief;
  const parts = [];
  const phone = mb?.facts?.phones?.[0];
  if (phone) {
    parts.push(`<phone>${phone}</phone>`);
  }
  if (mb?.copyrightText) {
    parts.push(`<copyright>${mb.copyrightText}</copyright>`);
  }
  const sl = mb?.socialLinks;
  if (sl) {
    const links = Object.entries(sl)
      .filter(([, v]) => v)
      .map(([k, v]) => `${k}: ${v}`);
    if (links.length) {
      parts.push(`<social_links>\n${links.join('\n')}\n</social_links>`);
    }
  }
  const email = ST.notificationEmail;
  if (email) {
    parts.push(`<notification_email>${email}</notification_email>`);
  }
  return parts.length ? `<business_context>\n${parts.join('\n')}\n</business_context>` : '';
}

/**
 * Resolve logo URL from all available sources.
 * @returns {string | null}
 */
function resolveLogoSrc() {
  const mb = ST.marketingBrief;
  if (ST.logoImg?.data && ST.logoImg.analysis?.useDecision !== 'skip') {
    return '__UPLOADED_LOGO__';
  }
  if (ST.logoUrl?.trim()) {
    return `/api/img-proxy?src=${encodeURIComponent(ST.logoUrl.trim())}`;
  }
  if (mb?.logoUrl?.trim() && /^https?:\/\//.test(mb.logoUrl)) {
    return `/api/img-proxy?src=${encodeURIComponent(mb.logoUrl.trim())}`;
  }
  return null;
}

/**
 * Format available images as XML context for section prompts.
 * sectionId: optional -- if provided, stock images are filtered to show only
 * images assigned to this section + unassigned general pool.
 * Without filtering, ALL stock images go to every section (token waste + duplication).
 * @param {import('../types.ts').Concept} c
 * @param {string} [sectionId]
 * @returns {string}
 */
function buildImageContext(c, sectionId) {
  const lines = ['<available_images>'];
  const mb = ST.marketingBrief;
  const existingImages = (mb?.images || []).filter(i => i.src && !i.src.startsWith('data:'));
  const hasExistingImages = existingImages.length > 0;

  // Filter stock images: only those assigned to this section + unassigned (_general)
  const allStock = (ST.stockImages || []).filter(i => !i.excluded);
  const sectionStock = sectionId
    ? allStock.filter(i => i.forSection === sectionId || i.forSection === '_general')
    : allStock;
  const hasStockImages = sectionStock.length > 0;

  // Logo
  const logoSrc = resolveLogoSrc();
  if (logoSrc) {
    lines.push(`<logo url="${logoSrc}" alt="${c.siteName || 'Logo'}" />`);
  } else {
    lines.push(`<logo>No logo provided — use site name "${c.siteName || 'Brand'}" as text</logo>`);
  }

  const isRebrand = ST.startPath === 'existing' && ST.existingOpt === 'rebrand';

  const fmtExisting = (img, i) => {
    const proxied = `/api/img-proxy?src=${encodeURIComponent(img.src)}`;
    return `  E${i + 1}. ${proxied}${img.alt ? ` — ${img.alt}` : ''}${img.category ? ` [${img.category}]` : ''}`;
  };
  const fmtStock = (img, i) =>
    `  S${i + 1}. ${img.srcMedium || img.src} — ${img.alt || 'stock photo'}`;

  if (hasExistingImages && hasStockImages) {
    if (isRebrand) {
      lines.push('<stock_images priority="primary">');
      sectionStock.slice(0, 5).forEach((img, i) => lines.push(fmtStock(img, i)));
      lines.push('</stock_images>');
      lines.push('<existing_images priority="optional">');
      existingImages.slice(0, 10).forEach((img, i) => lines.push(fmtExisting(img, i)));
      lines.push('</existing_images>');
    } else {
      lines.push('<existing_images priority="primary">');
      existingImages.slice(0, 15).forEach((img, i) => lines.push(fmtExisting(img, i)));
      lines.push('</existing_images>');
      if (sectionStock.length) {
        lines.push('<stock_images priority="backup">');
        sectionStock.slice(0, 3).forEach((img, i) => lines.push(fmtStock(img, i)));
        lines.push('</stock_images>');
      }
    }
  } else if (hasExistingImages) {
    const priority = isRebrand ? 'optional' : 'primary';
    lines.push(`<existing_images priority="${priority}">`);
    existingImages.slice(0, 15).forEach((img, i) => lines.push(fmtExisting(img, i)));
    lines.push('</existing_images>');
  } else if (hasStockImages) {
    lines.push('<stock_images priority="primary">');
    sectionStock.slice(0, 5).forEach((img, i) => lines.push(fmtStock(img, i)));
    lines.push('</stock_images>');
  }

  // Uploaded images — filter by placement matching this section
  if (ST.uploads?.images?.length) {
    const usable = ST.uploads.images.filter(i => i.analysis?.useDecision === 'use');
    const forSection = sectionId
      ? usable.filter(i => {
          const p = (i.analysis?.placement || '').toLowerCase();
          return (
            p.includes(sectionId) || (!sectionId.startsWith('section-') && p.includes(sectionId))
          );
        })
      : usable;
    // Show section-matched uploads + any without specific placement
    const unplaced = usable.filter(
      i => !i.analysis?.placement || i.analysis.placement === 'page imagery',
    );
    const toShow = [...new Set([...forSection, ...unplaced])];
    if (toShow.length) {
      lines.push('<uploaded_images priority="highest">');
      toShow.forEach(img => {
        const idx = usable.indexOf(img);
        lines.push(`  U${idx + 1}. __UPLOADED_${idx + 1}__ — "${img.name}"`);
      });
      lines.push('</uploaded_images>');
    }
  }

  const hasUploads = ST.uploads?.images?.some(i => i.analysis?.useDecision === 'use');
  if (!(hasExistingImages || hasStockImages || hasUploads)) {
    lines.push(
      '<no_images>Use CSS-only visuals: layered gradients, geometric patterns, radial glows.</no_images>',
    );
  }

  lines.push('<image_rules>');
  lines.push(
    '- Use exclusively the image URLs listed above — invented URLs produce broken images.',
  );
  lines.push(
    '- Match images to section purpose: [hero] category → hero section, [team] → about, [product] → features/services, [logo] → header/footer.',
  );
  lines.push('- Prefer existing over stock unless rebrand mode.');
  lines.push('</image_rules>');

  lines.push('</available_images>');

  return lines.join('\n');
}

// ── MODULE EXPORTS (activate when switching to ES modules) ──────
export {
  buildBusinessContext,
  buildImageContext,
  buildMarketingBriefContext,
  buildTokenContext,
  resolveLogoSrc,
  syncFlatTokens,
  tokensToMap,
};
